import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, FileText, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { formatDay } from "../../lib/finance";
import { canSeeRecords, canWrite, recordsError } from "../../lib/records";
import { StateBadge } from "./RecordBits";
import { Button } from "../ui/button";
import { Card } from "../ui/card";

const SHOWN = 5;

function useDocuments(visible, params) {
  const [state, setState] = useState(null);
  const key = JSON.stringify(params);
  useEffect(() => {
    if (!visible) return undefined;
    let alive = true;
    api.get("/records/documents", { params: { ...params, limit: SHOWN } })
      .then(({ data }) => { if (alive) setState(data); })
      .catch(() => { if (alive) setState({ error: true }); });
    return () => { alive = false; };
  }, [visible, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

function DocumentLine({ doc }) {
  return (
    <Link to={`/documents/${doc.id}`} className="flex items-start justify-between gap-2 rounded-lg -mx-2 px-2 py-1.5 hover:bg-slate-50" data-testid={`related-record-${doc.number}`}>
      <div className="min-w-0 space-y-1">
        <div className="text-sm font-medium text-slate-900 break-words">{doc.title}</div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <StateBadge state={doc.state} />{doc.expiry_date && <span>Expire le {formatDay(doc.expiry_date)}</span>}
        </div>
      </div>
      <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
    </Link>
  );
}

// D-71: a signed client contract while the customer account has no signature date — offered, never copied by itself. The write is
// the customer account's own PATCH, with its own rights (the button only shows when the account says « edit »).
function SignatureDateOffer({ account, docs, onChanged }) {
  const [busy, setBusy] = useState(false);
  const signed = docs.find((d) => d.type_code === "client_contract" && d.signature?.signed_on);
  if (!signed || account.contract_signed_at || !account.capabilities?.edit) return null;
  const report = async () => {
    setBusy(true);
    try {
      await api.patch(`/customers/${account.prospect_id}`, { expected_version: account.version, contract_signed_at: signed.signature.signed_on });
      toast.success("Date de signature reportée sur le dossier client.");
      onChanged?.();
    } catch (e) {
      toast.error(recordsError(e, "Impossible de reporter la date."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 space-y-2" data-testid="customer-records-signature-offer">
      <p>Le contrat {signed.number} est signé le {formatDay(signed.signature.signed_on)} ; le dossier client n'a pas de date de signature.</p>
      <Button size="sm" variant="outline" onClick={report} disabled={busy} data-testid="customer-records-report-signature">
        {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Reporter la date de signature
      </Button>
    </div>
  );
}

// Customer account (D-70): the documents of this pharmacy THE READER can see — the register's own rule decides.
export function CustomerRecordsCard({ account, onChanged }) {
  const { user } = useAuth();
  const visible = canSeeRecords(user);
  const data = useDocuments(visible, { party_ref: account.prospect_id });
  if (!visible || !data || data.error) return null;
  return (
    <Card className="p-5 space-y-3" data-testid="customer-records-card">
      <h3 className="flex items-center gap-2 font-semibold text-slate-900"><FileText size={16} className="text-slate-400" />Documents et contrats</h3>
      <SignatureDateOffer account={account} docs={data.items} onChanged={onChanged} />
      {data.items.length === 0 && <p className="text-sm text-slate-500">Aucun document en cours pour cette pharmacie.</p>}
      <div className="space-y-1">{data.items.map((d) => <DocumentLine key={d.id} doc={d} />)}</div>
      <div className="flex flex-wrap items-center gap-2">
        {data.total > SHOWN && <Link to={`/documents?party=${account.prospect_id}`} className="text-sm text-jamora hover:underline">Voir les {data.total} documents</Link>}
        {canWrite(user, "records:create") && (
          <Button asChild size="sm" variant="outline" data-testid="customer-records-new"><Link to={`/documents/nouveau?prospect=${account.prospect_id}`}>Nouveau contrat</Link></Button>
        )}
      </div>
    </Card>
  );
}

// Customer subscription (D-72): the contract linked to it, when the reader can see it.
export function SubscriptionContractCard({ subscriptionId }) {
  const { user } = useAuth();
  const visible = canSeeRecords(user);
  const data = useDocuments(visible, { subscription_id: subscriptionId, include_closed: true });
  if (!visible || !data || data.error) return null;
  return (
    <Card className="p-4 space-y-2" data-testid="subscription-contract-card">
      <h2 className="flex items-center gap-2 font-semibold text-slate-900"><FileText size={16} className="text-slate-400" />Contrat</h2>
      {data.items.length === 0 && <p className="text-sm text-slate-500">Aucun contrat rattaché à cet abonnement.</p>}
      <div className="space-y-1">{data.items.map((d) => <DocumentLine key={d.id} doc={d} />)}</div>
      {canWrite(user, "records:create") && (
        <Button asChild size="sm" variant="outline" data-testid="subscription-contract-new"><Link to={`/documents/nouveau?subscription=${subscriptionId}`}>Nouveau contrat</Link></Button>
      )}
    </Card>
  );
}
